import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Pagination,
  PaginationItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  Grid,
} from "@mui/material";
import {
  CancelOutlined,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  MoreHoriz,
} from "@mui/icons-material";
import {
  apiCallAvatarsList,
  apiCallAvatarsAdd,
  apiCallAvatarsUpdate,
  apiCallStatusUpdate,
} from "../../../../Core/Apicall";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../Redux/Store";
import { clearShowMenu } from "../../../../Redux/Reducers/GlobalVar";
import { UploadImages } from "../../../../Core/UploadImages";
import uploadicon from "../../../../assets/Images/Businesses/Upload-Normal.png";
import DeletePopup from "../../../Common/DeletePopup/DeletePopup";
import CommonMenu from "../../../Common/CommonMenu/CommonMenu";
import CommonStatus from "../../../Common/CommonStatus/CommonStatus";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";
import { toast } from 'react-toastify';

interface AvatarType {
  _id?: string;
  avatarName: string;
  gender: string;
  displayOrder: number | string;
  iconPath: string;
  isActive: boolean;
  isDelete: boolean;
}

function Avatars() {
  const [data, setData] = useState<AvatarType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState<string>("");
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const showMenu: any = useSelector(
    (state: RootState) => state.global.showMenu
  );
  const userdata: any = useSelector((state: RootState) => state.global.token);

  const [isCreateNew, setIsCreateNew] = useState<boolean>(!!showMenu);
  useEffect(() => {
    setIsCreateNew(!!showMenu);
  }, [showMenu]);

  const [formData, setFormData] = useState<any>({
    avatarId: "",
    avatarName: "",
    gender: "Male",
    displayOrder: "",
    iconPath: "",
    isActive: true,
    isDelete: false,
  });
  const [preview, setPreview] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuVibe, setMenuVibe] = useState<AvatarType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const totalItems = data.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  // 📌 Fetch Avatar list
  useEffect(() => {
    const fetchAvatars = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiCallAvatarsList("");
        if (response?.data) {
          setData(response.data);
        } else {
          setData([]);
        }
      } catch (err: any) {
        setError("Failed to fetch Avatars");
      } finally {
        setLoading(false);
      }
    };
    fetchAvatars();
  }, [page, rowsPerPage]);

  // 📌 Input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  // 📌 File upload (preview)
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      const res = await UploadImages(
        file,
        "preferences",
        userdata?.data?.userId
      );
      if (res?.location) {
        setFormData((prev: any) => ({ ...prev, iconPath: res?.location }));
      }
    }
  };

  // 📌 Submit form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (formData.avatarId) {
        // iconPath: "",
        const data = {
          adminId: userdata?.data?.userId,
          avatarId: formData.avatarId,
          avatarName: formData.avatarName,
          gender: formData.gender,
          displayOrder: Number(formData.displayOrder),
          iconPath: formData.iconPath,
        };

        await apiCallAvatarsUpdate(data);
        toast.success("Avatar update successfully!");
      } else {
        await apiCallAvatarsAdd(formData);
        toast.success("Avatar saved successfully!");
      }
      setIsCreateNew(false);
      setFormData({
        avatarId: "",
        avatarName: "",
        gender: "Male",
        displayOrder: "",
        iconPath: "",
        isActive: true,
        isDelete: false,
      });
      setPreview("");
      // refresh list
      const response = await apiCallAvatarsList("");
      setData(response?.data || []);
    } catch (err) {
      toast.error("Failed to save Avatar");
    }
  };

  // 📌 Toggle status
  const handleStatusUpdate = async (item: any) => {
    try {
      await apiCallStatusUpdate({
        adminId: userdata?.data?.userId,
        typeId: item._id,
        type: "louca_avatar",
        isActive: !item.isActive,
      });
      setData((prev) =>
        prev.map((a) =>
          a._id === item._id ? { ...a, isActive: !item.isActive } : a
        )
      );
      setStatusDialogOpen(false);
    } catch (err) {
    }
  };

  const handleDelete = async () => {
    if (!menuVibe) return;
    try {
      await apiCallStatusUpdate({
        adminId: userdata?.data?.userId,
        type: "louca_avatar",
        typeId: menuVibe._id,
        isDelete: true,
      });
      setData((prev) => prev.filter((item) => item._id !== menuVibe._id));
    } catch (err) {
    } finally {
      setDeleteDialogOpen(false);
      setMenuVibe(null);
    }
  };

  // ------------------- Menu Handlers -------------------
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, type: any) => {
    setAnchorEl(event.currentTarget);
    setMenuVibe(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    {
      label: "Edit",
      onClick: () => {
        if (menuVibe) {
          setFormData({
            ...menuVibe,
            avatarId: menuVibe._id,
          });
          setPreview(menuVibe.iconPath || "");
          setIsCreateNew(true);
        }
        handleMenuClose();
      },
    },
    {
      label: "Delete",
      onClick: () => setDeleteDialogOpen(true),
    },
  ];

  return (
    <Box style={{ paddingBottom: "20px" }} >
      <Box className="d-flex input align-items-center justify-content-between">
        <TextField
          className="border-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ backgroundColor: "white" }}
          placeholder="Search Louca Avatars"
        />
        <Button
          className="btn-black p-2 px-3"
          onClick={() => setIsCreateNew(true)}
        >
          + Create New
        </Button>
      </Box>

      <Paper
        className="bg-white mt-3 border-20 scroll-bar"
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <TableContainer className="scroll-bar" >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Display Order</TableCell>
                <TableCell>Avatar Name</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Icon</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data
                .filter(
                  (item) =>
                    search === "" ||
                    item.avatarName.toLowerCase().includes(search.toLowerCase())
                )
                .map((item, index: any) => (
                  <TableRow hover key={index}>
                    <TableCell>{item.displayOrder}</TableCell>
                    <TableCell>{item.avatarName}</TableCell>
                    <TableCell>{item.gender}</TableCell>
                    <TableCell>
                      {item.iconPath && (
                        <img
                          src={item.iconPath}
                          alt="icon"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: "6px",
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        className={
                          item?.isActive ? "btn-success" : "btn-danger"
                        }
                        onClick={() => setStatusDialogOpen(index)}
                      >
                        {item?.isActive ? "Active" : "Inactive"}
                      </Button>
                      <CommonStatus
                        open={statusDialogOpen === index}
                        title={item?.avatarName}
                        message={`Are you sure you want to ${item?.isActive ? "deactivate" : "activate"
                          } this venue?`}
                        confirmText={item?.isActive ? "Deactivate" : "Activate"}
                        confirmColor={item?.isActive ? "warning" : "success"}
                        onConfirm={() => handleStatusUpdate(item)}
                        onClose={() => setStatusDialogOpen(false)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={(e) => handleMenuOpen(e, item)}>
                        <MoreHoriz />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {loading && (
            <div style={{ padding: "10px 10px" }}>
              <CommonShimmer />
            </div>
          )}
        </TableContainer>

      </Paper>

      <CommonMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        items={menuItems}
      />

      <DeletePopup
        open={deleteDialogOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this avatar?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onClose={() => setDeleteDialogOpen(false)}
      />

      <Drawer
        anchor="right"
        open={isCreateNew}
        onClose={() => {
          setIsCreateNew(false);
          dispatch(clearShowMenu());
        }}
        PaperProps={{
          sx: {
            width: "35%",
            borderRadius: "16px 0 0 16px",
            overflow: "hidden",
          },
        }}
      >
        <Box
          className="avatars-form"
          height="100%"
          display="flex"
          flexDirection="column"
        >
          {/* ---------- Header ---------- */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={3}
            py={2}
            borderBottom="1px solid #eee"
            bgcolor="#fafafa"
          >
            <Typography variant="h6" fontWeight={600}>
              {formData.avtarsId ? "Edit Avatar" : "Create Avatar"}
            </Typography>
            <IconButton
              onClick={() => {
                setIsCreateNew(false);
                dispatch(clearShowMenu());
              }}
            >
              <CancelOutlined />
            </IconButton>
          </Box>

          {/* ---------- Form ---------- */}
          <Box flex={1} px={3} py={2} overflow="auto">
            <form onSubmit={handleSubmit}>
              {/* Avatar Name */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Avatar Name
                </Typography>
                <TextField
                  name="avatarName"
                  value={formData.avatarName}
                  onChange={handleChange}
                  placeholder="Enter Avatar Name"
                  fullWidth
                />
              </FormControl>

              <Grid size={4} sx={{ mb: 3 }}>
                <TextField
                  select
                  label="Avatar Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  fullWidth
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>

              {/* Display Order */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Display Order
                </Typography>
                <TextField
                  name="displayOrder"
                  type="number"
                  value={formData.displayOrder || ""}
                  onChange={handleChange}
                  placeholder="Enter Display Order"
                  fullWidth
                />
              </FormControl>

              {/* Icon Upload */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Icon
                </Typography>
                <Box
                  sx={{
                    cursor: "pointer",
                    border: "2px dashed #ccc",
                    borderRadius: "12px",
                    p: 4,
                    textAlign: "center",
                    transition: "0.3s",
                    "&:hover": { borderColor: "#000" },
                  }}
                  position="relative"
                >
                  <input
                    type="file"
                    id="file-upload"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                  <label htmlFor="file-upload" style={{ cursor: "pointer" }}>
                    <img
                      src={uploadicon}
                      alt="Upload Icon"
                      style={{
                        width: 50,
                        height: 50,
                        objectFit: "contain",
                        opacity: 0.8,
                      }}
                    />
                  </label>
                </Box>

                {preview && (
                  <Box mt={2}>
                    <Typography variant="body2" mb={1}>
                      Preview:
                    </Typography>
                    <img
                      src={preview}
                      alt="preview"
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 8,
                        objectFit: "cover",
                        border: "1px solid #ddd",
                      }}
                    />
                  </Box>
                )}
              </FormControl>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: "10px",
                  py: 1.2,
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: "#000",
                  "&:hover": { backgroundColor: "#222" },
                }}
              >
                Save
              </Button>
            </form>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

export default Avatars;
